import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Camera, Film, Plane, Zap, Crown, Star, ArrowRight, Check } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

const SERVICES = [
  {
    icon: Camera,
    title: "Signature Event Photography",
    price: "From $3,500",
    desc: "Every frame a masterwork. Our photographers combine editorial precision with artistic vision to create images that transcend documentation — they become legacy.",
    features: ["Full event coverage (8-12 hours)", "Professional editing & retouching", "Private online gallery", "Premium prints available", "Same-day preview selects"],
    image: "https://images.unsplash.com/photo-1775036191163-a3fbb14a1370?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBldmVudCUyMHBob3RvZ3JhcGh5JTIwbmlnaHR8ZW58MHx8fHwxNzc2MDU2NDk0fDA&ixlib=rb-4.1.0&q=85"
  },
  {
    icon: Film,
    title: "Cinematic Videography",
    price: "From $5,000",
    desc: "Your event deserves a director. Our cinematic team captures your occasion with the production quality of a feature film — complete with storytelling, pacing, and emotional depth.",
    features: ["Multi-camera cinema setup", "Professional audio capture", "4K+ resolution", "Cinematic color grading", "Highlight reel + full edit"],
    image: "https://images.unsplash.com/photo-1561721632-a0d70ac1d15b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxjaW5lbWElMjBjYW1lcmElMjBkYXJrJTIwc3R1ZGlvfGVufDB8fHx8MTc3NjA1NjQ5NHww&ixlib=rb-4.1.0&q=85"
  },
  {
    icon: Plane,
    title: "Drone Coverage",
    price: "From $2,500",
    desc: "Perspective changes everything. Our licensed drone operators deliver breathtaking aerial footage that reveals the full grandeur of your venue and event from extraordinary vantage points.",
    features: ["Licensed & insured operators", "4K aerial footage", "Dynamic tracking shots", "Venue showcase reels", "Compliance with all regulations"],
    image: "https://images.unsplash.com/photo-1641900833685-2874c5ebd974?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxkcm9uZSUyMGZseWluZyUyMG9jZWFuJTIwbmlnaHR8ZW58MHx8fHwxNzc2MDU2NDk0fDA&ixlib=rb-4.1.0&q=85"
  },
  {
    icon: Zap,
    title: "High-Tech Event Capture",
    price: "From $4,000",
    desc: "The future of event documentation. Utilizing AI-enhanced cameras, real-time editing systems, and immersive capture technology to create an unprecedented record of your event.",
    features: ["AI-enhanced photography", "Real-time editing suite", "360-degree capture option", "Live streaming capability", "Interactive digital galleries"],
    image: null
  },
  {
    icon: Crown,
    title: "VIP Event Packages",
    price: "$8,000 - $25,000+",
    desc: "The complete luxury experience. Our VIP packages combine every service into a seamless, white-glove production that handles every visual element of your event.",
    features: ["Dedicated production team", "All photography & videography", "Drone aerial coverage", "Same-day social media content", "Priority editing & delivery"],
    image: null
  },
  {
    icon: Star,
    title: "Custom Premium Experiences",
    price: "By Consultation",
    desc: "For events that defy categorization. Our bespoke service designs a completely custom capture strategy tailored to your unique vision, venue, and objectives.",
    features: ["Personalized consultation", "Custom team composition", "Bespoke deliverables", "Unlimited creative direction", "White-glove project management"],
    image: null
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-luxury-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Our Services</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-8">
              Luxury Event Coverage<br /><span className="gold-gradient-text">at the Highest Level</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-base leading-relaxed">
              Each service is crafted to deliver an unparalleled standard of quality, technology, and artistry.
              We don't offer packages — we offer experiences.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-luxury-charcoal">
        {SERVICES.map((svc, i) => (
          <div
            key={svc.title}
            data-testid={`service-${i}`}
            className={`py-24 md:py-32 ${i % 2 === 0 ? "bg-luxury-charcoal" : "bg-luxury-black"}`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${i % 2 !== 0 ? "lg:flex-row-reverse" : ""}`}>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className={i % 2 !== 0 ? "lg:order-2" : ""}>
                  <motion.div variants={fadeUp} custom={0} className="flex items-center gap-3 mb-6">
                    <svc.icon size={24} className="text-gold" />
                    <span className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold">{svc.price}</span>
                  </motion.div>
                  <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl font-light text-white mb-6">{svc.title}</motion.h2>
                  <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-sm leading-relaxed mb-8">{svc.desc}</motion.p>
                  <motion.ul variants={fadeUp} custom={3} className="space-y-3 mb-8">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-white/70 text-sm font-body font-light">
                        <Check size={14} className="text-gold flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </motion.ul>
                  <motion.div variants={fadeUp} custom={4}>
                    <Link
                      to={`/booking?service=${encodeURIComponent(svc.title)}`}
                      className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors"
                    >
                      Inquire Now <ArrowRight size={16} />
                    </Link>
                  </motion.div>
                </motion.div>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className={i % 2 !== 0 ? "lg:order-1" : ""}>
                  {svc.image ? (
                    <motion.div variants={fadeUp} custom={1} className="aspect-[4/3] overflow-hidden">
                      <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" loading="lazy" />
                    </motion.div>
                  ) : (
                    <motion.div variants={fadeUp} custom={1} className="aspect-[4/3] glass-card flex items-center justify-center">
                      <svc.icon size={80} className="text-gold/20" />
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
